import {
  migrateGlobalToOrgScoped, readOrgScoped, writeOrgScoped,
} from '../../hooks/orgScopedStorage';

export type DashboardProduct = 'qutime' | 'pureoa';
export type WidgetLicense = 'neutral' | DashboardProduct;

export interface DashboardItemState {
  id: string;
  type: string;
  i18nKey?: string;
  title?: string;
  data?: unknown;
}

export interface DashboardLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  maxW?: number;
  minH?: number;
  maxH?: number;
  static?: boolean;
  moved?: boolean;
}

export interface DashboardState {
  items: DashboardItemState[];
  layout: DashboardLayoutItem[];
}

export interface WidgetDefinition {
  type: string;
  i18nKey: string;
  license: WidgetLicense;
  defaultSize: { w: number; h: number };
  catalogOrder: number;
}

/** Single source of truth for widget metadata used by titles, picker and gates. */
export const WIDGET_CONFIG: Record<string, WidgetDefinition> = {
  'quick-actions': { type: 'quick-actions', i18nKey: 'quickActions', license: 'neutral', defaultSize: { w: 4, h: 6 }, catalogOrder: 10 },
  'organization-overview': { type: 'organization-overview', i18nKey: 'organizationOverview', license: 'neutral', defaultSize: { w: 8, h: 6 }, catalogOrder: 20 },
  'my-pending-tasks': { type: 'my-pending-tasks', i18nKey: 'myPendingTasks', license: 'pureoa', defaultSize: { w: 6, h: 10 }, catalogOrder: 30 },
  'process-approval-queue': { type: 'process-approval-queue', i18nKey: 'approvalQueue', license: 'pureoa', defaultSize: { w: 8, h: 9 }, catalogOrder: 40 },
  // Every approval widget was pureoa-licensed, so a QuTime-only tenant had none
  // at all — while attendance approvals are that tenant's highest-frequency
  // actionable item (2026-08-05).
  'attendance-approval-queue': { type: 'attendance-approval-queue', i18nKey: 'attendanceApprovals', license: 'qutime', defaultSize: { w: 4, h: 9 }, catalogOrder: 45 },
  'attendance-summary': { type: 'attendance-summary', i18nKey: 'attendanceSummary', license: 'qutime', defaultSize: { w: 8, h: 7 }, catalogOrder: 50 },
  'attendance-live-monitor': { type: 'attendance-live-monitor', i18nKey: 'attendanceLiveMonitor', license: 'qutime', defaultSize: { w: 12, h: 10 }, catalogOrder: 60 },
  'process-statistics': { type: 'process-statistics', i18nKey: 'processStatistics', license: 'pureoa', defaultSize: { w: 8, h: 10 }, catalogOrder: 70 },
  'recently-visited': { type: 'recently-visited', i18nKey: 'recentlyVisited', license: 'neutral', defaultSize: { w: 4, h: 10 }, catalogOrder: 80 },
  'process-sla-status': { type: 'process-sla-status', i18nKey: 'processSla', license: 'pureoa', defaultSize: { w: 4, h: 10 }, catalogOrder: 90 },
  'recent-process-activity': { type: 'recent-process-activity', i18nKey: 'recentProcessActivity', license: 'pureoa', defaultSize: { w: 4, h: 12 }, catalogOrder: 100 },
  'attendance-geofences': { type: 'attendance-geofences', i18nKey: 'attendanceGeofences', license: 'qutime', defaultSize: { w: 6, h: 12 }, catalogOrder: 110 },
  'date-time': { type: 'date-time', i18nKey: 'dateTime', license: 'neutral', defaultSize: { w: 3, h: 6 }, catalogOrder: 120 },
  'access-control-overview': { type: 'access-control-overview', i18nKey: 'accessControl', license: 'neutral', defaultSize: { w: 3, h: 6 }, catalogOrder: 130 },
  'integrations-overview': { type: 'integrations-overview', i18nKey: 'integrationsOverview', license: 'neutral', defaultSize: { w: 4, h: 8 }, catalogOrder: 140 },
  'recent-audit-logs': { type: 'recent-audit-logs', i18nKey: 'recentAuditLogs', license: 'neutral', defaultSize: { w: 4, h: 8 }, catalogOrder: 150 },
  'today-birthday': { type: 'today-birthday', i18nKey: 'todayBirthday', license: 'neutral', defaultSize: { w: 3, h: 6 }, catalogOrder: 160 },
  'upcoming-events': { type: 'upcoming-events', i18nKey: 'upcomingEvents', license: 'neutral', defaultSize: { w: 4, h: 8 }, catalogOrder: 170 },
  'pending-gdpr-deletions': { type: 'pending-gdpr-deletions', i18nKey: 'pendingGdpr', license: 'neutral', defaultSize: { w: 4, h: 10 }, catalogOrder: 180 },
};

export const WIDGET_CATALOG = Object.values(WIDGET_CONFIG)
  .sort((a, b) => a.catalogOrder - b.catalogOrder);

const LEGACY_DEFAULT_LAYOUT: DashboardLayoutItem[] = [
  { i: 'quick-actions-1', x: 0, y: 0, w: 3, h: 6, minW: 2, minH: 5 },
  { i: 'organization-overview-1', x: 3, y: 0, w: 6, h: 6, minW: 2, minH: 5 },
  { i: 'date-time-1', x: 9, y: 0, w: 3, h: 6, minW: 2, minH: 5 },
  { i: 'access-control-overview-1', x: 0, y: 6, w: 3, h: 6, minW: 2, minH: 5 },
  { i: 'attendance-summary-1', x: 3, y: 6, w: 6, h: 6, minW: 2, minH: 5 },
  { i: 'today-birthday-1', x: 9, y: 6, w: 3, h: 6, minW: 2, minH: 5 },
  { i: 'upcoming-events-1', x: 0, y: 12, w: 4, h: 8, minW: 2, minH: 5 },
  { i: 'recent-audit-logs-1', x: 4, y: 12, w: 4, h: 8, minW: 2, minH: 5 },
  { i: 'integrations-overview-1', x: 8, y: 12, w: 4, h: 8, minW: 2, minH: 5 },
  { i: 'attendance-live-monitor-1', x: 0, y: 20, w: 6, h: 10, minW: 2, minH: 5 },
  { i: 'process-statistics-1', x: 6, y: 20, w: 6, h: 10, minW: 2, minH: 5 },
];

const LEGACY_DEFAULT_ITEMS: DashboardItemState[] = [
  { id: 'quick-actions-1', type: 'quick-actions', title: 'Quick Actions' },
  { id: 'organization-overview-1', type: 'organization-overview', title: 'Organization Overview' },
  { id: 'date-time-1', type: 'date-time', title: 'Date & Time' },
  { id: 'access-control-overview-1', type: 'access-control-overview', title: 'Access Control Summary' },
  { id: 'attendance-summary-1', type: 'attendance-summary', title: 'Attendance Summary' },
  { id: 'today-birthday-1', type: 'today-birthday', title: "Today's Birthdays" },
  { id: 'upcoming-events-1', type: 'upcoming-events', title: 'Upcoming Events' },
  { id: 'recent-audit-logs-1', type: 'recent-audit-logs', title: 'Recent Audit Logs' },
  { id: 'integrations-overview-1', type: 'integrations-overview', title: 'Integrations Summary' },
  { id: 'attendance-live-monitor-1', type: 'attendance-live-monitor', title: 'Attendance Live Monitor' },
  { id: 'process-statistics-1', type: 'process-statistics', title: 'Process Statistics' },
];

/** Exported only so migration behavior can be pinned without duplicating fixtures. */
export const LEGACY_DEFAULT_STATE: DashboardState = {
  items: LEGACY_DEFAULT_ITEMS,
  layout: LEGACY_DEFAULT_LAYOUT,
};

function recognizedProducts(products: readonly string[]): Set<DashboardProduct> {
  const normalized = new Set(products.map((product) => product.toLowerCase()));
  const recognized = new Set<DashboardProduct>();
  if (normalized.has('qutime')) recognized.add('qutime');
  if (normalized.has('pureoa')) recognized.add('pureoa');
  return recognized;
}

export function hasDashboardProductLicense(
  licensedProducts: readonly string[],
  product: DashboardProduct,
): boolean {
  return recognizedProducts(licensedProducts).has(product);
}

export function isWidgetAllowed(type: string, licensedProducts: readonly string[]): boolean {
  const definition = WIDGET_CONFIG[type];
  if (!definition || definition.license === 'neutral') return true;
  return hasDashboardProductLicense(licensedProducts, definition.license);
}

export function getAllowedWidgetCatalog(licensedProducts: readonly string[]): WidgetDefinition[] {
  return WIDGET_CATALOG.filter((widget) => isWidgetAllowed(widget.type, licensedProducts));
}

export function getWidgetTitleKey(type: string): string {
  const i18nKey = WIDGET_CONFIG[type]?.i18nKey ?? type;
  return `dashboard:widgets.${i18nKey}.title`;
}

function item(type: string): DashboardItemState {
  return { id: `${type}-1`, type, i18nKey: WIDGET_CONFIG[type].i18nKey };
}

function grid(type: string, x: number, y: number, w: number, h: number): DashboardLayoutItem {
  return { i: `${type}-1`, x, y, w, h, minW: 2, minH: 3 };
}

/**
 * The layout a tenant sees on day one, ordered by what an SMB admin does first.
 *
 * WHY THIS ORDER (2026-08-05)
 * --------------------------
 * Measured on the 13" baseline (svdy-ui §2, 1280x800): the grid has 668px above
 * the fold, and `rowHeight: 30` with `margin: 12` means h units cost
 * `h*30 + (h-1)*12`. The previous bundle default put four rows at h=6/9/9/10, so
 * only the FIRST FOUR of eight widgets were usable — row three showed a 38px
 * sliver and row four sat entirely below the fold.
 *
 * Worse than the count was the allocation: the first row's eight columns went to
 * `organization-overview`, a headcount tile an admin glances at monthly, while the
 * two things they open this page FOR were below it.
 *
 * An SMB admin's morning is two questions, and both decay: "is anyone late or not
 * clocked in?" is worth answering at 09:05 and worthless at 15:00, and "what is
 * waiting on me?" is blocking somebody else's day. Those go first, at the heights
 * a list actually needs. Headcount, statistics and the launcher follow, because
 * nothing about them is time-critical.
 *
 * THE FOLD IS A CONSTRAINT, NOT A PREFERENCE
 * `h=9` then `h=7` costs 366 + 12 + 282 = 660px, so two substantial rows fit with
 * 8px to spare. `dashboardConfig.test.ts` asserts it, because the arithmetic is
 * invisible in the literals below and the next person to add a widget will not
 * redo it.
 *
 * NO DATE & TIME BY DEFAULT — deliberately. The operating system already shows the
 * clock in its own status bar, so a default slot would spend the second-most
 * valuable position on the screen repeating it. The widget earns its place only
 * when the admin and the site are in different time zones, since it renders the
 * ORGANIZATION's zone; that is a real need and a per-tenant one, which is what the
 * widget picker is for.
 */
export function getDefaultDashboardState(licensedProducts: readonly string[]): DashboardState {
  const products = recognizedProducts(licensedProducts);
  let layout: DashboardLayoutItem[];

  if (products.has('qutime') && products.has('pureoa')) {
    layout = [
      // Row 1 — what decays: who is not clocked in, and who is waiting on a
      // decision about their own hours.
      grid('attendance-live-monitor', 0, 0, 8, 9), grid('attendance-approval-queue', 8, 0, 4, 9),
      // Row 2 — the other queue that blocks someone else's day.
      grid('process-approval-queue', 0, 9, 8, 7), grid('my-pending-tasks', 8, 9, 4, 7),
      grid('attendance-summary', 0, 16, 8, 7), grid('quick-actions', 8, 16, 4, 7),
      grid('process-statistics', 0, 23, 8, 10), grid('recently-visited', 8, 23, 4, 10),
      // Full width only at the very bottom, where it cannot create a ragged seam
      // mid-page. Headcount is a monthly glance.
      grid('organization-overview', 0, 33, 12, 6),
    ];
  } else if (products.has('qutime')) {
    layout = [
      grid('attendance-live-monitor', 0, 0, 8, 9), grid('attendance-approval-queue', 8, 0, 4, 9),
      grid('attendance-summary', 0, 9, 8, 7), grid('quick-actions', 8, 9, 4, 7),
      grid('attendance-geofences', 0, 16, 8, 10), grid('recently-visited', 8, 16, 4, 10),
      grid('organization-overview', 0, 26, 12, 6),
    ];
  } else if (products.has('pureoa')) {
    layout = [
      grid('process-approval-queue', 0, 0, 8, 9), grid('my-pending-tasks', 8, 0, 4, 9),
      grid('process-statistics', 0, 9, 8, 7), grid('quick-actions', 8, 9, 4, 7),
      grid('organization-overview', 0, 16, 8, 8), grid('recently-visited', 8, 16, 4, 8),
    ];
  } else {
    // Nothing to triage yet: the launcher leads, because this tenant is still
    // finding their way around.
    layout = [
      grid('organization-overview', 0, 0, 8, 9), grid('quick-actions', 8, 0, 4, 9),
      // h=6, not 8: h=9 + h=8 comes to 702px and the second row would cross the
      // fold — on the one layout where there are only two rows to see.
      grid('recently-visited', 0, 9, 12, 6),
    ];
  }

  return { items: layout.map((entry) => item(entry.i.slice(0, -2))), layout };
}

/** Pixel height of `h` grid units, given DashboardGrid's rowHeight 30 / margin 12. */
export function gridUnitsToPx(h: number): number {
  return h * 30 + (h - 1) * 12;
}

/**
 * Usable height above the fold on the 13" baseline, after the top bar, the page
 * header and the container padding. Measured from the source, not assumed.
 */
export const FOLD_BUDGET_PX = 668;

function isDashboardItem(value: unknown): value is DashboardItemState {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<DashboardItemState>;
  return typeof candidate.id === 'string' && typeof candidate.type === 'string';
}

function isLayoutItem(value: unknown): value is DashboardLayoutItem {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<DashboardLayoutItem>;
  return typeof candidate.i === 'string' &&
    [candidate.x, candidate.y, candidate.w, candidate.h].every((part) => typeof part === 'number' && Number.isFinite(part));
}

export function parseDashboardState(raw: string | null): DashboardState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<DashboardState>;
    if (!Array.isArray(parsed.items) || !parsed.items.every(isDashboardItem) ||
        !Array.isArray(parsed.layout) || !parsed.layout.every(isLayoutItem)) return null;
    return { items: parsed.items, layout: parsed.layout };
  } catch {
    return null;
  }
}

export function isExactLegacyDefault(state: DashboardState): boolean {
  if (state.items.length !== LEGACY_DEFAULT_ITEMS.length || state.layout.length !== LEGACY_DEFAULT_LAYOUT.length) return false;
  const itemsMatch = state.items.every((candidate, index) => {
    const legacy = LEGACY_DEFAULT_ITEMS[index];
    return candidate.id === legacy.id && candidate.type === legacy.type;
  });
  const geometryMatches = state.layout.every((candidate, index) => {
    const legacy = LEGACY_DEFAULT_LAYOUT[index];
    return candidate.i === legacy.i && candidate.x === legacy.x && candidate.y === legacy.y &&
      candidate.w === legacy.w && candidate.h === legacy.h;
  });
  return itemsMatch && geometryMatches;
}

export function resolveInitialDashboardState(raw: string | null, licensedProducts: readonly string[]): DashboardState {
  const stored = parseDashboardState(raw);
  if (!stored || isExactLegacyDefault(stored)) return getDefaultDashboardState(licensedProducts);
  return stored;
}

export function deriveVisibleDashboardState(state: DashboardState, licensedProducts: readonly string[]): DashboardState {
  const items = state.items.filter((candidate) => isWidgetAllowed(candidate.type, licensedProducts));
  const visibleIds = new Set(items.map((candidate) => candidate.id));
  return { items, layout: state.layout.filter((candidate) => visibleIds.has(candidate.i)) };
}

export function mergeVisibleLayout(
  fullLayout: DashboardLayoutItem[],
  visibleLayout: DashboardLayoutItem[],
  visibleItemIds: ReadonlySet<string>,
): DashboardLayoutItem[] {
  const visibleById = new Map(visibleLayout.map((entry) => [entry.i, entry]));
  const merged = fullLayout.map((entry) => visibleItemIds.has(entry.i) ? (visibleById.get(entry.i) ?? entry) : entry);
  const knownIds = new Set(fullLayout.map((entry) => entry.i));
  return [...merged, ...visibleLayout.filter((entry) => !knownIds.has(entry.i))];
}

// ---------------------------------------------------------------------------
// Per-organization persistence (2026-08-05)
// ---------------------------------------------------------------------------
//
// P0: the layout was stored under ONE global key, `dashboard-state-v2`, with no
// organization in it. An admin who belongs to several orgs therefore had one
// layout shared across all of them, and because each org licenses different
// products, `deriveVisibleDashboardState` filtered out whatever the new org was
// not entitled to. The visible result is a grid that reflows into the holes —
// reported as widgets "drifting" after every switch.
//
// It also explains why "Reset to default layout" appeared to fix it and why the
// fix did not survive: reset wrote the NEW org's default into the same global
// key, so switching back handed that layout to the previous org.
//
// The shape is a `Record<orgId, DashboardState>` under one key, which is the
// pattern `useRecentlyVisited` already uses for exactly this problem. Following
// it rather than minting `dashboard-state-<orgId>` keys keeps one entry to
// migrate, one to clear on sign-out, and no unbounded key growth.

const DASHBOARD_STATE_KEY = 'dashboard-state-v3';
/** The unscoped key this replaces. Adopted once during migration, then removed. */
const LEGACY_GLOBAL_KEY = 'dashboard-state-v2';

/**
 * This organization's saved layout, or null.
 *
 * Delegates to `hooks/orgScopedStorage`, which is the same shape
 * `useRecentlyVisited` has always used: one key holding a `Record<orgId, T>`. It was
 * a single global value, so an admin with several orgs had ONE layout across all of
 * them — and because each licenses different products, the widgets the new org lacked
 * were filtered out and the grid reflowed into the holes. That is the reported
 * "widgets drift after switching organization", including the part that made it
 * confusing: Reset appeared to fix it because reset wrote the NEW org's default into
 * the same shared key, so switching back handed that layout to the previous org.
 *
 * The migration is one-shot — see `migrateGlobalToOrgScoped`.
 */
export function loadDashboardState(
  orgId: string | null,
  storage: Storage = localStorage,
): DashboardState | null {
  const scoped = readOrgScoped<DashboardState>(DASHBOARD_STATE_KEY, orgId, storage);
  if (scoped) return scoped;
  return migrateGlobalToOrgScoped<DashboardState>(
    LEGACY_GLOBAL_KEY, DASHBOARD_STATE_KEY, parseDashboardState, orgId, storage,
  );
}

export function saveDashboardState(
  orgId: string | null,
  state: DashboardState,
  storage: Storage = localStorage,
): void {
  writeOrgScoped(DASHBOARD_STATE_KEY, state, orgId, storage);
}
